import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2, Clock, Wrench, PackageCheck, XCircle,
  Camera, MessageCircle, MapPin, Phone, AlertTriangle,
} from "lucide-react";
import { checklistFor, STATUS_LABEL, CHECKLIST_STATUS } from "../lib/phoneCatalog";

const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  recebido: { icon: PackageCheck, color: "text-slate-600", bg: "bg-slate-100" },
  em_reparo: { icon: Wrench, color: "text-amber-700", bg: "bg-amber-100" },
  pronto: { icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-100" },
  entregue: { icon: CheckCircle2, color: "text-blue-700", bg: "bg-blue-100" },
  cancelado: { icon: XCircle, color: "text-red-700", bg: "bg-red-100" },
};

const CHECK_CLR = {};
CHECKLIST_STATUS.forEach((c) => (CHECK_CLR[c.value] = c));

export default function TrackOrder() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!id) return;
    axios
      .get(`${BACKEND}/api/public/track/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "OS não encontrada"));
  }, [id]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white p-8 rounded-sm border border-slate-200 text-center">
          <XCircle size={40} className="text-red-500 mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-900 mb-2">Ordem não encontrada</h1>
          <p className="text-sm text-slate-600">{err}</p>
          <p className="text-xs text-slate-400 mt-4">Verifique o link com a loja.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Carregando...</div>
      </div>
    );
  }

  const timeline = data.status_timeline || [];
  const currentIdx = data.status === "cancelado" ? -1 : timeline.indexOf(data.status);
  const items = checklistFor(data.brand, data.device);
  const check = data.checklist || {};

  // Compute overdue if pronto
  let overdue = null;
  if (data.status === "pronto" && data.storage_start) {
    const daysStored = Math.floor(
      (Date.now() - new Date(data.storage_start).getTime()) / 86400000
    );
    const daysOverdue = Math.max(0, daysStored - 3);
    if (daysOverdue > 0) {
      overdue = { daysStored, daysOverdue, fee: daysOverdue };
    }
  }

  const fullPhoto = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

  const waLink = () => {
    const clean = String(data.store?.phone || "").replace(/\D/g, "");
    const num = clean.length === 11 || clean.length === 10 ? `55${clean}` : clean;
    const text = `Olá! Sobre a OS *#${data.short_id}* (${data.brand} ${data.device})...`;
    return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="track-page">
      {/* Header */}
      <header className="bg-gradient-to-br from-[#0A1628] to-[#2563EB] text-white py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.3em] text-blue-200 mb-1">
            {data.store?.name || "UP CEL Quilombo"}
          </div>
          <h1 className="font-display text-3xl font-bold">Acompanhamento de OS</h1>
          <div className="text-blue-100 text-sm mt-1">
            Nº <span className="font-mono font-bold" data-testid="track-short-id">#{data.short_id}</span>
            {" · "}Cliente: <span className="font-semibold">{data.customer_display}</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-5 -mt-4">
        {/* Card principal do dispositivo */}
        <div className="bg-white rounded-sm border border-slate-200 p-5" data-testid="track-device">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">Aparelho</div>
          <h2 className="font-display text-2xl font-bold text-slate-900">
            {data.brand ? data.brand + " " : ""}
            {data.device}
          </h2>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Problema relatado</div>
              <div className="text-slate-800">{data.problem || "—"}</div>
            </div>
            {data.diagnosis && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Serviço executado</div>
                <div className="text-slate-800">{data.diagnosis}</div>
              </div>
            )}
            {data.price > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Valor</div>
                <div className="text-lg font-bold text-[#2563EB]" data-testid="track-price">
                  {brl(data.price)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status Timeline */}
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Status atual</div>
          {data.status === "cancelado" ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 rounded-sm border border-red-200">
              <XCircle size={24} className="text-red-600" />
              <div>
                <div className="font-bold text-red-700">Ordem Cancelada</div>
                <div className="text-xs text-red-600">Entre em contato com a loja para mais informações.</div>
              </div>
            </div>
          ) : (
            <div className="relative grid grid-cols-4 gap-2" data-testid="track-timeline">
              {/* Linha de progresso conectando os passos */}
              <div className="absolute top-5 left-[12.5%] right-[12.5%] h-0.5 bg-slate-200" />
              <div
                className="absolute top-5 left-[12.5%] h-0.5 bg-[#2563EB] transition-all"
                style={{
                  width:
                    currentIdx >= 0
                      ? `${(currentIdx / (timeline.length - 1)) * 75}%`
                      : "0%",
                }}
              />
              {timeline.map((st, idx) => {
                const meta = STATUS_META[st];
                const active = idx <= currentIdx;
                const current = idx === currentIdx;
                const Icon = meta.icon;
                return (
                  <div key={st} className="flex flex-col items-center text-center relative z-10">
                    <div
                      className={`w-11 h-11 rounded-full flex items-center justify-center border-2 transition bg-white ${
                        active
                          ? `${meta.bg} ${meta.color} border-current`
                          : "text-slate-300 border-slate-200"
                      } ${current ? "ring-4 ring-blue-100 scale-110" : ""}`}
                      data-testid={`track-step-${st}${current ? "-current" : ""}`}
                    >
                      <Icon size={18} />
                    </div>
                    <div
                      className={`text-[10px] uppercase tracking-wider mt-2 font-semibold ${
                        active ? "text-slate-800" : "text-slate-400"
                      }`}
                    >
                      {STATUS_LABEL[st] || st.replace("_", " ")}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {overdue && (
            <div
              className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-start gap-2"
              data-testid="track-overdue"
            >
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <b>Aparelho pronto há {overdue.daysStored} dias.</b>
                <br />
                Taxa de armazenamento acumulada:{" "}
                <b>{brl(overdue.fee)}</b> ({overdue.daysOverdue} dia{overdue.daysOverdue !== 1 && "s"} × R$ 1,00).
                Passe na loja o quanto antes para retirar.
              </div>
            </div>
          )}
        </div>

        {/* Fotos */}
        {(data.photos_entry?.length > 0 || data.photos_exit?.length > 0) && (
          <div className="bg-white rounded-sm border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Camera size={16} className="text-[#2563EB]" />
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Fotos do aparelho</div>
            </div>
            {data.photos_entry?.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-semibold text-slate-700 mb-2">📸 Entrada</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {data.photos_entry.map((u, i) => (
                    <a
                      key={u}
                      href={fullPhoto(u)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square rounded-sm overflow-hidden border border-slate-200 hover:border-[#2563EB]"
                      data-testid={`track-photo-entry-${i}`}
                    >
                      <img src={fullPhoto(u)} alt="Entrada" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {data.photos_exit?.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-slate-700 mb-2">✅ Saída (após reparo)</div>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {data.photos_exit.map((u, i) => (
                    <a
                      key={u}
                      href={fullPhoto(u)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square rounded-sm overflow-hidden border border-slate-200 hover:border-emerald-500"
                      data-testid={`track-photo-exit-${i}`}
                    >
                      <img src={fullPhoto(u)} alt="Saída" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Checklist */}
        {Object.keys(check).length > 0 && (
          <div className="bg-white rounded-sm border border-slate-200 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
              Checklist do recebimento
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items
                .filter((it) => check[it.key])
                .map((it) => {
                  const s = check[it.key]?.status;
                  const meta = CHECK_CLR[s];
                  return (
                    <div
                      key={it.key}
                      className="flex justify-between items-center py-1.5 px-3 border border-slate-100 rounded-sm text-sm"
                      data-testid={`track-check-${it.key}`}
                    >
                      <span className="text-slate-700">{it.label}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${meta?.cls || ""}`}>
                        {meta?.label || s}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Loja */}
        <div className="bg-white rounded-sm border border-slate-200 p-5">
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
            Precisa falar com a gente?
          </div>
          <div className="text-lg font-display font-bold mb-2">{data.store?.name}</div>
          {data.store?.address && (
            <div className="text-sm text-slate-600 flex items-start gap-2 mb-1">
              <MapPin size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
              {data.store.address}
            </div>
          )}
          {data.store?.phone && (
            <div className="text-sm text-slate-600 flex items-center gap-2 mb-3">
              <Phone size={14} className="text-slate-400" />
              {data.store.phone}
            </div>
          )}
          {data.store?.phone && (
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="track-wa-btn"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 rounded-sm text-sm"
            >
              <MessageCircle size={14} /> Falar no WhatsApp
            </a>
          )}
        </div>

        <div className="text-center text-xs text-slate-400 py-4">
          Acompanhamento gerado por{" "}
          <span className="font-semibold">{data.store?.name}</span>
        </div>
      </main>
    </div>
  );
}
